import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

/**
 * De onde a forma de pagamento é aceita. null = todas as origens.
 *
 * Os baldes são excludentes: um pedido de delivery ou de mesa digitado pelo atendente é
 * REGISTRADOR, e não CLIENTE_DELIVERY nem CLIENTE_MESA. CLIENTE cobre delivery, retirada e mesa.
 *
 * Substitui o antigo tipoEntregaAceita ("DELIVERY" | "REGISTRADOR"), onde DELIVERY na verdade
 * significava "canal do cliente". O backend ainda envia aquela chave como alias deprecated, mas
 * dá precedência a esta quando ela está presente.
 */
export type OrigemPedidoAceita =
    | "CLIENTE_DELIVERY"
    | "CLIENTE_RETIRADA"
    | "CLIENTE_MESA"
    | "CLIENTE"
    | "REGISTRADOR"
    | null

export interface FormaPagamentoItem {
    idFormaPagamento: number
    descricaoFormaPagamento: string
    /** Nome padrao (global) da forma; usado como sugestao/placeholder do nome customizado. */
    descricaoPadrao?: string | null
    /** Nome customizado SUGERIDO no cadastro global; vira placeholder/valor efetivo se a loja nao definir o seu. */
    descricaoPersonalizadaSugerida?: string | null
    /** Nome customizado que a loja definiu; null/vazio = usa o sugerido global ou, na falta, o padrao. */
    descricaoPersonalizada?: string | null
    associado: boolean
    ordemExib: number
    desconto: number | null
    tipoPdv: string | null
    codigoPdv: string | null
    chavePix: string | null
    tipoChavePix: string | null
    codigoQrCodePix: string | null
    origemPedidoAceita: OrigemPedidoAceita
    pix: boolean
}

export interface FormasPagamentoResposta {
    formasPagamento: FormaPagamentoItem[]
}

export interface SalvarFormasPagamentoPayload {
    formasPagamento: FormaPagamentoItem[]
}

export interface SalvarFormasPagamentoResposta {
    sucesso: boolean
    avisos: string[]
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

const ErroRequisicao = function (error: { message?: string }): string | false {
    if (error.message === "Network Error") return "Verifique sua conexão!"
    if (error.message) return "Erro: " + error.message
    return false
}

export const obterFormasPagamento = async function (
    authorization: string
): Promise<FormasPagamentoResposta | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<FormasPagamentoResposta>>(
                "formaPagamentoConfig/ObterFormasPagamento",
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                const msg = ErroRequisicao(error)
                return {
                    data: {
                        codErro: 1,
                        mensagem: msg || "Erro desconhecido",
                    } as RespostaRaw<FormasPagamentoResposta>,
                }
            })

        return tratamentoResponse<FormasPagamentoResposta>(response, "ObterFormasPagamento")
    } catch (error) {
        RegistrarLogErro(error, "ObterFormasPagamento", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarFormasPagamento = async function (
    authorization: string,
    payload: SalvarFormasPagamentoPayload
): Promise<SalvarFormasPagamentoResposta | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<SalvarFormasPagamentoResposta>>(
                "formaPagamentoConfig/SalvarFormasPagamento",
                payload,
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                const msg = ErroRequisicao(error)
                return {
                    data: {
                        codErro: 1,
                        mensagem: msg || "Erro desconhecido",
                    } as RespostaRaw<SalvarFormasPagamentoResposta>,
                }
            })

        return tratamentoResponse<SalvarFormasPagamentoResposta>(response, "SalvarFormasPagamento")
    } catch (error) {
        RegistrarLogErro(error, "SalvarFormasPagamento", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
